<template>
  <ion-page>
    <ion-header>
      <ion-toolbar class="toolbar-header">
        <div class="d-flex align-center">
          <ion-buttons slot="start" @click="$emit('close-modal')">
            <ion-button fill="clear" color="primary">
              <ion-icon slot="icon-only" :icon="chevronBackOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-label class="fw-600 fs-3">{{ $t('voucher_code') }}</ion-label>
        </div>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <!-- manual input section -->
      <div class="pa-3 bg-white">
        <div class="d-flex align-center" style="gap: 8px">
          <ion-input
            v-model="manualCode"
            :placeholder="$t('enter_voucher_code')"
            class="border-bottom"
            style="flex: 1"
            tabindex="-1"
            @keyup.enter="applyManualCode"
          ></ion-input>
          <ion-button
            fill="outline"
            size="small"
            :disabled="!manualCode.trim() || applying"
            @click="applyManualCode"
          >
            {{ applying ? $t('loading') : $t('apply') }}
          </ion-button>
        </div>
        <div v-if="errorMsg" class="mt-2">
          <ion-label color="danger" class="fs-2">{{ errorMsg }}</ion-label>
        </div>
        <div class="mt-2">
          <ion-label color="medium" class="fs-1">* {{ $t('voucher_min_order_note') }}</ion-label>
        </div>
      </div>

      <ion-item-divider style="min-height: 1px" />

      <!-- available vouchers section -->
      <div class="pa-3">
        <ion-label class="fw-600 fs-3">{{ $t('available_vouchers') }}</ion-label>
      </div>

      <div v-if="loading" class="d-flex justify-center pa-4">
        <ion-spinner name="crescent" color="primary"></ion-spinner>
      </div>

      <div v-else-if="availableVouchers.length === 0" class="pa-4 text-center">
        <ion-label color="medium" class="fs-2">{{ $t('no_vouchers_available') }}</ion-label>
      </div>

      <div v-else class="px-3 pb-3" style="display: flex; flex-direction: column; gap: 12px">
        <div
          v-for="voucher in availableVouchers"
          :key="voucher.code"
          class="voucher-card"
          :class="{ 'voucher-card--applied': appliedVoucherCode === voucher.code }"
        >
          <div class="d-flex justify-space-between align-center">
            <div style="flex: 1">
              <div class="d-flex align-center" style="gap: 6px">
                <ion-label color="primary" class="fw-bold fs-3">{{ voucher.code }}</ion-label>
                <ion-chip
                  v-if="appliedVoucherCode === voucher.code"
                  class="applied-chip ion-no-margin"
                  color="primary"
                >
                  <ion-label>{{ $t('applied') }}</ion-label>
                </ion-chip>
              </div>
              <ion-label color="dark" class="fs-2 d-block mt-1">{{ voucher.name }}</ion-label>
              <ion-label color="medium" class="fs-2 d-block mt-1">
                <span v-if="voucher.type === 'percentage'">{{ voucher.percentageValue }}% OFF</span>
                <span v-else-if="voucher.type === 'money'">
                  {{ priceFormatter(currencySymbol, voucher.discountAmount) }} OFF
                </span>
                <span v-else-if="voucher.type === 'sku'">
                  {{ $t('free_items') }}:
                  {{ voucher.freeItems.map((i) => `${i.skuName} x${i.quantity}`).join(', ') }}
                </span>
              </ion-label>
            </div>
            <div class="ml-2">
              <ion-button
                v-if="appliedVoucherCode !== voucher.code"
                fill="outline"
                size="small"
                :disabled="applying"
                @click="selectVoucher(voucher)"
              >
                {{ $t('use') }}
              </ion-button>
              <ion-button
                v-else
                fill="clear"
                size="small"
                color="danger"
                @click="$emit('voucher-selected', null)"
              >
                {{ $t('remove') }}
              </ion-button>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { apolloClient } from '@/main';
import { checkVoucher, getAvailableVouchers } from '@/modules/sale/services/graphql';
import { priceFormatter } from '@/utils/';
import { chevronBackOutline } from 'ionicons/icons';
import { defineComponent, ref } from 'vue';

export default defineComponent({
  name: 'ModalVoucher',
  emits: ['close-modal', 'voucher-selected'],
  props: {
    customerId: { type: Number, required: true },
    tenantId: { type: Number, required: true },
    orderTotal: { type: Number, required: true },
    currencySymbol: { type: String, default: '' },
    appliedVoucherCode: { type: String, default: null }
  },
  setup() {
    const manualCode = ref('');
    const errorMsg = ref('');
    const applying = ref(false);
    const loading = ref(false);
    const availableVouchers = ref([]);

    return {
      manualCode,
      errorMsg,
      applying,
      loading,
      availableVouchers,
      chevronBackOutline,
      priceFormatter
    };
  },
  async mounted() {
    this.loading = true;
    try {
      const { data } = await apolloClient.query({
        query: getAvailableVouchers,
        variables: {
          customerId: this.customerId,
          tenantId: this.tenantId,
          orderTotal: this.orderTotal
        }
      });
      this.availableVouchers = data.getAvailableVouchers ?? [];
    } catch {
      this.availableVouchers = [];
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async applyManualCode() {
      if (!this.manualCode.trim() || this.applying) return;
      await this.validateAndSelect(this.manualCode.trim().toUpperCase());
    },
    async selectVoucher(voucher) {
      await this.validateAndSelect(voucher.code);
    },
    async validateAndSelect(code) {
      this.applying = true;
      this.errorMsg = '';
      try {
        const { data } = await apolloClient.query({
          query: checkVoucher,
          variables: {
            voucherCode: code,
            customerId: this.customerId,
            tenantId: this.tenantId,
            orderTotal: this.orderTotal
          }
        });
        const result = data.checkVoucher;
        if (result.success) {
          this.$emit('voucher-selected', result.voucher);
        } else {
          this.errorMsg = result.message;
        }
      } catch {
        this.errorMsg = this.$t('something_went_wrong_please_try_again');
      } finally {
        this.applying = false;
      }
    }
  }
});
</script>

<style scoped>
.voucher-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}
.voucher-card--applied {
  border-color: #04565a;
  background: #e6f2f2;
}
.applied-chip {
  height: 20px;
  font-size: 11px;
}
</style>
